/**
 * Helpers around the Web Share API.
 *
 * navigator.share() only works while the click's user activation is still alive, so the image is
 * fetched ahead of time and kept in a cache. The click handler can then hand the file over
 * synchronously instead of awaiting a download first (large graphics outlive the activation).
 */

const fileCache = new Map();

const fileNameFromUrl = (url, fallback = 'grafik') => {
    try {
        const name = new URL(url, window.location.origin).pathname.split('/').filter(Boolean).pop();
        return name && name.includes('.') ? name : fallback;
    } catch {
        return fallback;
    }
};

export const fetchShareFile = async (url, fileName = fileNameFromUrl(url)) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Grafik konnte nicht geladen werden (${response.status})`);
    const blob = await response.blob();
    const type = blob.type || 'image/jpeg';
    // Asset URLs without a file extension (e.g. image proxies) still need a usable download name.
    const name = /\.[a-z0-9]+$/i.test(fileName) ? fileName : `${fileName}.${type.split('/')[1] || 'jpg'}`;
    return new File([blob], name, { type });
};

/** Loads the image into the cache so a later click can share it without waiting. */
export const prefetchShareFile = (url, fileName) => {
    if (!url || fileCache.has(url)) return;
    fetchShareFile(url, fileName)
        .then((file) => fileCache.set(url, file))
        .catch(() => {
            // Without a fetchable image the share falls back to the page URL.
        });
};

export const getCachedShareFile = (url) => (url ? fileCache.get(url) : undefined);

/** True when the browser can open a native share sheet. */
export const canUseNativeShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function';

export const saveFile = (file) => {
    const objectUrl = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking immediately can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

/**
 * Opens the native share sheet for the page, optionally with the configured graphic.
 * Browsers without the Web Share API (or without file support) fall back to downloading the image,
 * which mirrors the previous behaviour on desktop. Without an image there is nothing to download.
 */
export const shareImage = ({ imageUrl, fileName, title, text, url }) => {
    const file = getCachedShareFile(imageUrl);

    const downloadFallback = () => {
        if (file) {
            saveFile(file);
            return;
        }
        if (imageUrl) window.open(imageUrl, '_blank', 'noopener,noreferrer');
    };

    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
        downloadFallback();
        return;
    }

    const shareData = { title, text, url };
    const payload = file && navigator.canShare?.({ files: [file] }) ? { ...shareData, files: [file] } : shareData;

    let pending;
    try {
        pending = navigator.share(payload);
    } catch {
        downloadFallback();
        return;
    }

    // A cancelled share dialog is not an error the user needs to hear about.
    pending?.catch((error) => {
        if (error?.name !== 'AbortError') downloadFallback();
    });
};
